import React, { createContext, useState, useContext, useEffect } from 'react';

const OddsFormatContext = createContext();

export const useOddsFormat = () => {
  const context = useContext(OddsFormatContext);
  if (!context) {
    throw new Error('useOddsFormat must be used within an OddsFormatProvider');
  }
  return context;
};

// Odds conversion functions
export const convertOdds = (decimalOdds, toFormat) => {
  if (!decimalOdds || decimalOdds < 1) return '-';
  
  const decimal = parseFloat(decimalOdds);
  
  switch (toFormat) {
    case 'decimal':
      return decimal.toFixed(2);
    
    case 'fractional':
      return decimalToFractional(decimal);
    
    case 'american':
      return decimalToAmerican(decimal);
    
    default:
      return decimal.toFixed(2);
  }
};

const decimalToFractional = (decimal) => {
  if (decimal < 1) return '-';
  
  const fraction = decimal - 1;
  const tolerance = 1.0E-6;
  let numerator = 1;
  let denominator = 1;
  let error = Math.abs(fraction - numerator / denominator);
  
  while (error > tolerance && denominator < 1000) {
    if (fraction > numerator / denominator) {
      numerator++;
    } else {
      denominator++;
      numerator = Math.round(fraction * denominator);
    }
    error = Math.abs(fraction - numerator / denominator);
  }
  
  // Simplify the fraction
  const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
  const divisor = gcd(numerator, denominator);
  numerator = numerator / divisor;
  denominator = denominator / divisor;
  
  return `${numerator}/${denominator}`;
};

const decimalToAmerican = (decimal) => {
  if (decimal < 1) return '-';
  
  if (decimal >= 2) {
    return `+${Math.round((decimal - 1) * 100)}`;
  } else {
    return `-${Math.round(100 / (decimal - 1))}`;
  }
};

// Format display with label
export const formatOddsDisplay = (decimalOdds, format) => {
  const converted = convertOdds(decimalOdds, format);
  return converted;
};

// Get placeholder text for odds input
export const getOddsPlaceholder = (format) => {
  switch (format) {
    case 'decimal':
      return 'e.g., 2.50';
    case 'fractional':
      return 'e.g., 3/2';
    case 'american':
      return 'e.g., +150 or -200';
    default:
      return 'e.g., 2.50';
  }
};

// Convert input odds to decimal (for backend storage)
export const convertToDecimal = (odds, fromFormat) => {
  if (!odds) return null;
  
  switch (fromFormat) {
    case 'decimal':
      return parseFloat(odds);
    
    case 'fractional':
      return fractionalToDecimal(odds);
    
    case 'american':
      return americanToDecimal(odds);
    
    default:
      return parseFloat(odds);
  }
};

const fractionalToDecimal = (fractional) => {
  const parts = fractional.toString().split('/');
  if (parts.length !== 2) return null;
  
  const numerator = parseFloat(parts[0]);
  const denominator = parseFloat(parts[1]);
  
  if (isNaN(numerator) || isNaN(denominator) || denominator === 0) return null;
  
  return 1 + (numerator / denominator);
};

const americanToDecimal = (american) => {
  const value = parseFloat(american);
  if (isNaN(value)) return null;
  
  if (value >= 100) {
    return 1 + (value / 100);
  } else if (value <= -100) {
    return 1 + (100 / Math.abs(value));
  }
  
  return null;
};

export const OddsFormatProvider = ({ children, user }) => {
  const [oddsFormat, setOddsFormat] = useState(() => {
    return localStorage.getItem('oddsFormat') || 'decimal';
  });

  // Sync with user's database preference when user data is available
  useEffect(() => {
    if (user?.odds_format) {
      setOddsFormat(user.odds_format);
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem('oddsFormat', oddsFormat);
  }, [oddsFormat]);

  const value = {
    oddsFormat,
    setOddsFormat,
    convertOdds: (decimalOdds) => convertOdds(decimalOdds, oddsFormat),
    formatOddsDisplay: (decimalOdds) => formatOddsDisplay(decimalOdds, oddsFormat),
    getOddsPlaceholder: () => getOddsPlaceholder(oddsFormat),
    convertToDecimal: (odds) => convertToDecimal(odds, oddsFormat)
  };

  return (
    <OddsFormatContext.Provider value={value}>
      {children}
    </OddsFormatContext.Provider>
  );
};

export default OddsFormatContext;
